import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import BusComponent from '../../BusComponent';
import Mixpanel, { MIXPANEL_EVENTS, PIN_CONFIG_VIEW_TRIGGER } from '../../../helpers/Mixpanel';

import VisibilityToggle from '../../form/VisibilityToggle';
import GeoJsonLibraryLegendItem from './GeoJsonLibraryLegendItem';
import AppConfig from '../../../appConfig';
import Toggle from '../../form/Toggle';
import FacilityPinConfig, {
    PIN_CONFIG_LABELS,
    LISTING_STATUS_COLORS,
    LISTING_STATUS_LAYER_IDS,
    LISTING_STATUS_LAYER_PROPERTY,
    PRICING_COLORS,
    DEVELOPMENT_PIN_FILL,
    BY_TYPE_HIGHLIGHT_METRICS,
    BY_TYPE_LAYER_IDS,
    BY_TYPE_LAYER_PROPERTY,
    HIGHLIGHT_COLOR,
} from '../../../enums/FacilityPinConfig';
import { saveMapDefaults } from '../../../helpers/mapDefaults';
import {
    DEFAULT_BY_TYPE_VISIBILITY,
    byTypeLayerVisibility,
    isTypeSplit,
    reconcileByTypeVisibility,
} from '../../../helpers/FacilityPinVisibilityHelper';

// Classic (revived teardrop) legend rows — labels mirror the points metadata's
// own legend block; colors are the faded teardrop sprite fills (category-based:
// pricing=green, nopricing=red, construction=yellow).
const CLASSIC_LEGEND_ROWS = [
    { key: 'pricing', label: 'Self storage with pricing', color: '#65A37F' },
    { key: 'nopricing', label: 'Self storage facility', color: '#DA6D6E' },
    { key: 'construction', label: 'Development site', color: '#FBC02D' },
];

const DEFAULT_CLASSIC_VISIBILITY = CLASSIC_LEGEND_ROWS.reduce((acc, r) => {
    acc[r.key] = true;
    return acc;
}, {});

// "By type" legend types (no status rows). Each one renders a plain row, plus a
// highlighted row when the active highlight metric can match it — see renderByTypeLegend.
// Colors read from the shared BY_TYPE_MARKERS which reuse warehouse_onmarket
// (green) and development_onmarket (yellow/green) sprites.
const BY_TYPE_GROUPS = [
    {
        key: 'operating',
        title: 'Operating facility',
        icon: 'warehouse',
        shape: 'circle',
        // Green — matches warehouse_onmarket fill (#178f65 = LISTING_STATUS_COLORS.onMarket)
        color: LISTING_STATUS_COLORS.onMarket,
    },
    {
        key: 'development',
        title: 'Development site',
        icon: 'construction',
        shape: 'square',
        // Yellow fill + white outline + white glyph — matches the
        // development_bytype_plain sprite. No `color` here: the square swatch
        // hardcodes DEVELOPMENT_PIN_FILL + white (only the circle swatch reads `color`).
    },
];

const BY_TYPE_HIGHLIGHT_METRIC_LABELS = {
    [BY_TYPE_HIGHLIGHT_METRICS.LISTED]: 'Listed on TractIQ',
    [BY_TYPE_HIGHLIGHT_METRICS.ON_MARKET]: 'Likely on market',
    [BY_TYPE_HIGHLIGHT_METRICS.HAS_PRICING]: 'Has pricing',
};

// Suffix appended to a type's title on its highlighted row, e.g. "Operating facility ·
// listed on TractIQ". Composed with the group titles instead of a per-type × per-metric
// label matrix so there is only one string per metric to keep in sync.
const BY_TYPE_HIGHLIGHT_ROW_SUFFIX = {
    [BY_TYPE_HIGHLIGHT_METRICS.LISTED]: 'listed on TractIQ',
    [BY_TYPE_HIGHLIGHT_METRICS.ON_MARKET]: 'likely on market',
    [BY_TYPE_HIGHLIGHT_METRICS.HAS_PRICING]: 'with pricing',
};

const LISTING_STATUS_GROUPS = [
    { key: 'operating', title: 'Operating facility', icon: 'warehouse', shape: 'circle' },
    { key: 'development', title: 'Development site', icon: 'construction', shape: 'square' },
];

const LISTING_STATUS_ROWS = [
    { key: 'listed', label: 'Listed on platform', color: LISTING_STATUS_COLORS.listed },
    { key: 'onMarket', label: 'Likely On Market', color: LISTING_STATUS_COLORS.onMarket },
    { key: 'offMarket', label: 'Off market', color: LISTING_STATUS_COLORS.offMarket },
];

const DEFAULT_LISTING_STATUS_VISIBILITY = Object.values(LISTING_STATUS_LAYER_IDS).reduce(
    (acc, group) => {
        Object.values(group).forEach(id => {
            acc[id] = true;
        });
        return acc;
    },
    {},
);

// "By pricing" legend (1.2 design). Same pin structure as listing status, but the
// rows map 1:1 to the `category` feature property (the legend's filter property),
// so each row's `key` IS the category value used for show/hide. Development sites
// are under construction (no operational rates) → No Pricing only.
const PRICING_GROUPS = [
    {
        key: 'operating',
        title: 'Operating facility',
        icon: 'warehouse',
        shape: 'circle',
        rows: [
            { key: 'pricing', label: 'Has Pricing', color: PRICING_COLORS.hasPricing },
            { key: 'nopricing', label: 'No Pricing', color: PRICING_COLORS.noPricing },
        ],
    },
    {
        key: 'development',
        title: 'Development site',
        icon: 'construction',
        shape: 'square',
        rows: [{ key: 'construction', label: 'No Pricing', color: PRICING_COLORS.noPricing }],
    },
];

const DEFAULT_PRICING_VISIBILITY = PRICING_GROUPS.reduce((acc, group) => {
    group.rows.forEach(row => {
        acc[row.key] = true;
    });
    return acc;
}, {});

class GeoJsonLibraryLegend extends BusComponent {
    constructor(props, context) {
        super(props, context);

        const { layers } = props.geoJsonLegendMetadata;

        const { mapInstance } = props;
        this.state = {
            useClusters: false,
            visible: (mapInstance && mapInstance.facilityPinsVisible) !== false,
            clusteringDisabled: true,
            items: layers.map(l => ({ ...l })),
            pinConfig: (mapInstance && mapInstance.facilityPinConfig) || FacilityPinConfig.LEGACY,
            isConfigMenuOpen: false,
            // Per-row show/hide state for the listing-status legend; persisted
            // on the map instance like pinConfig.
            listingStatusVisibility: (mapInstance && mapInstance.listingStatusLayerVisibility) || {
                ...DEFAULT_LISTING_STATUS_VISIBILITY,
            },
            // Per-row show/hide for the "by pricing" legend; persisted on the map
            // instance like listingStatusVisibility. Keyed by `category` value.
            pricingVisibility: (mapInstance && mapInstance.pricingLayerVisibility) || {
                ...DEFAULT_PRICING_VISIBILITY,
            },
            // "By type" variant: orange outline highlight. Single-select on/off —
            // the active metric, or null when nothing is highlighted (the default).
            // The chips are always shown in Beta mode; there is no separate on/off
            // toggle, so `null` is the "no highlight" state.
            byTypeHighlightMetric: (mapInstance && mapInstance.byTypeHighlightMetric) || null,
            // "By type" show/hide, one entry per legend row (type × highlighted-or-not),
            // keyed by BY_TYPE_LAYER_IDS. Persisted on the map instance like the other
            // legends. The plain rows are the non-highlighted pins, so with a metric
            // active hiding "Operating facility" hides only the operating facilities that
            // metric does not match.
            byTypeVisibility: (mapInstance && mapInstance.byTypeLayerVisibility) || {
                ...DEFAULT_BY_TYPE_VISIBILITY,
            },
            // Classic (LEGACY) per-row show/hide — mirrors pricingVisibility but for
            // the teardrop legend. Keyed by `category` value (pricing/nopricing/construction).
            classicVisibility: (mapInstance && mapInstance.classicLayerVisibility) || {
                ...DEFAULT_CLASSIC_VISIBILITY,
            },
        };
    }

    // The 6 listing-status virtual layers as the layerVisibility payload shape
    // used by UPDATE_GEO_JSON_LAYER_VISIBILITY / PointsHandler filtering.
    listingStatusLayerVisibility(visibilityMap) {
        return Object.keys(DEFAULT_LISTING_STATUS_VISIBILITY).map(layerId => ({
            layerId,
            visible: visibilityMap[layerId] !== false,
        }));
    }

    // Pricing rows filter on the `category` feature property directly, so the
    // layerIds are the category values ('pricing' / 'nopricing' / 'construction').
    pricingLayerVisibility(visibilityMap) {
        return Object.keys(DEFAULT_PRICING_VISIBILITY).map(layerId => ({
            layerId,
            visible: visibilityMap[layerId] !== false,
        }));
    }

    // Classic rows also filter on `category` — same three keys. Independent from
    // pricingLayerVisibility so hiding in Classic doesn't carry into Pricing.
    classicLayerVisibility(visibilityMap) {
        return Object.keys(DEFAULT_CLASSIC_VISIBILITY).map(layerId => ({
            layerId,
            visible: visibilityMap[layerId] !== false,
        }));
    }

    onTogglePinConfigMenu = () => {
        this.setState(state => ({ isConfigMenuOpen: !state.isConfigMenuOpen }));
    };

    onSelectPinConfig = config => {
        const { mapInstance, mapInstanceId, geoJsonLegendMetadata } = this.props;
        const { listingStatusVisibility, pricingVisibility, classicVisibility } = this.state;
        if (mapInstance) {
            mapInstance.facilityPinConfig = config;
        }
        saveMapDefaults(this.bus, { facilityPinConfig: config });
        this.setState({ pinConfig: config, isConfigMenuOpen: false });
        // On-toggle counterpart of the load-time impression in
        // PointsHandler#_trackInitialPinConfig: same event, trigger:'user_toggle' marks a
        // deliberate user switch (vs trigger:'load' for the default on load).
        // `highlight` is Beta-only — attach it only when switching INTO by-type
        // (read the current/restored highlight metric); other modes have no highlight,
        // so the property stays absent there (matches Legacy = null/absent).
        Mixpanel.emitUserEvent(MIXPANEL_EVENTS.VIEW_FACILITY_PIN_CONFIG, {
            pinConfig: config,
            label: PIN_CONFIG_LABELS[config],
            trigger: PIN_CONFIG_VIEW_TRIGGER.USER_TOGGLE,
            ...(config === FacilityPinConfig.BY_TYPE && {
                highlight: this.getHighlightLabel(this.state.byTypeHighlightMetric),
            }),
        });

        // Each config filters features on its own property, so the per-row
        // show/hide filter has to switch together with the pins. Listing status
        // uses the derived `listing_status_layer`; by-type uses the derived
        // `by_type_layer` (type × highlighted); pricing filters on the raw
        // `category` property (the legend's default filter property).
        // Classic (LEGACY) also filters on `category` — use its own state so
        // hiding in Classic doesn't bleed into Pricing and vice-versa.
        const isListingStatus = config === FacilityPinConfig.LISTING_STATUS;
        const isByType = config === FacilityPinConfig.BY_TYPE;
        const isClassic = config === FacilityPinConfig.LEGACY;
        this.emit('MAP_APPLY_FACILITY_PIN_CONFIG', {
            config,
            mapInstanceId,
            featurePropertyNameEquivalentToLayerId: isListingStatus
                ? LISTING_STATUS_LAYER_PROPERTY
                : isByType
                  ? BY_TYPE_LAYER_PROPERTY
                  : geoJsonLegendMetadata.featurePropertyNameEquivalentToLayerId,
            layerVisibility: isListingStatus
                ? this.listingStatusLayerVisibility(listingStatusVisibility)
                : isByType
                  ? byTypeLayerVisibility(
                        this.state.byTypeVisibility,
                        this.state.byTypeHighlightMetric,
                    )
                  : isClassic
                    ? this.classicLayerVisibility(classicVisibility)
                    : this.pricingLayerVisibility(pricingVisibility),
            byTypeHighlightMetric: isByType ? this.state.byTypeHighlightMetric : null,
        });
    };

    getHighlightLabel = metric => (metric ? BY_TYPE_HIGHLIGHT_METRIC_LABELS[metric] : 'None');

    // Sets the active highlight metric (or null = "Highlight by: None"). Single
    // source for the picker: the "None" option passes null, the metric chips pass
    // their metric, and clicking the active metric chip clears back to null.
    applyByTypeHighlightMetric = nextMetric => {
        const { mapInstance, mapInstanceId } = this.props;
        const prevMetric = this.state.byTypeHighlightMetric;
        // The metric decides which rows exist, so hidden rows have to be carried across
        // the change (a hidden type stays hidden when it splits in two).
        const byTypeVisibility = reconcileByTypeVisibility(
            this.state.byTypeVisibility,
            prevMetric,
            nextMetric,
        );

        if (mapInstance) {
            mapInstance.byTypeHighlightMetric = nextMetric;
            mapInstance.byTypeLayerVisibility = byTypeVisibility;
        }
        this.setState({ byTypeHighlightMetric: nextMetric, byTypeVisibility });

        Mixpanel.emitUserEvent(MIXPANEL_EVENTS.VIEW_FACILITY_PIN_CONFIG, {
            pinConfig: FacilityPinConfig.BY_TYPE,
            label: PIN_CONFIG_LABELS[FacilityPinConfig.BY_TYPE],
            trigger: PIN_CONFIG_VIEW_TRIGGER.HIGHLIGHT_SELECT,
            highlight: this.getHighlightLabel(nextMetric),
        });

        // The reconciled visibility rides along so the pins re-classify and re-filter in a
        // single pass — emitting UPDATE_GEO_JSON_LAYER_VISIBILITY separately would render
        // one frame filtered on the previous metric's split.
        this.emit('MAP_APPLY_HIGHLIGHT', {
            mapInstanceId,
            highlightVisible: Boolean(nextMetric),
            metric: nextMetric,
            featurePropertyNameEquivalentToLayerId: BY_TYPE_LAYER_PROPERTY,
            layerVisibility: byTypeLayerVisibility(byTypeVisibility, nextMetric),
        });
    };

    // Metric chip click: clicking the active metric clears it, clicking another
    // switches to it (single-select on/off).
    onByTypeHighlightMetricToggle = metric => {
        this.applyByTypeHighlightMetric(this.state.byTypeHighlightMetric === metric ? null : metric);
    };

    // Click-to-hide on a single by-type row. Each row is one virtual layer, so there is no
    // group/child relationship: the plain rows hide the pins the active metric doesn't
    // match, the highlighted rows hide the ones it does.
    onByTypeRowToggle = layerId => {
        const { mapInstance, mapInstanceId } = this.props;
        const byTypeVisibility = {
            ...this.state.byTypeVisibility,
            [layerId]: this.state.byTypeVisibility[layerId] === false,
        };

        if (mapInstance) {
            mapInstance.byTypeLayerVisibility = byTypeVisibility;
        }
        this.setState({ byTypeVisibility });

        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId: BY_TYPE_LAYER_PROPERTY,
            layerVisibility: byTypeLayerVisibility(
                byTypeVisibility,
                this.state.byTypeHighlightMetric,
            ),
            mapInstanceId,
        });
    };

    onClassicRowToggle = layerId => {
        const { mapInstance, mapInstanceId, geoJsonLegendMetadata } = this.props;
        const classicVisibility = {
            ...this.state.classicVisibility,
            [layerId]: this.state.classicVisibility[layerId] === false,
        };
        if (mapInstance) mapInstance.classicLayerVisibility = classicVisibility;
        this.setState({ classicVisibility });
        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId:
                geoJsonLegendMetadata.featurePropertyNameEquivalentToLayerId,
            layerVisibility: this.classicLayerVisibility(classicVisibility),
            mapInstanceId,
        });
    };

    onListingStatusRowToggle = layerId => {
        const { mapInstance, mapInstanceId } = this.props;
        const listingStatusVisibility = {
            ...this.state.listingStatusVisibility,
            [layerId]: this.state.listingStatusVisibility[layerId] === false,
        };

        // Persist on the map instance so state survives legend close/reopen
        if (mapInstance) {
            mapInstance.listingStatusLayerVisibility = listingStatusVisibility;
        }
        this.setState({ listingStatusVisibility });

        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId: LISTING_STATUS_LAYER_PROPERTY,
            layerVisibility: this.listingStatusLayerVisibility(listingStatusVisibility),
            mapInstanceId: mapInstanceId,
        });
    };

    onListingStatusGroupToggle = groupKey => {
        const { mapInstance, mapInstanceId } = this.props;
        const groupLayerIds = Object.values(LISTING_STATUS_LAYER_IDS[groupKey]);
        const anyVisible = groupLayerIds.some(
            layerId => this.state.listingStatusVisibility[layerId] !== false,
        );

        const listingStatusVisibility = { ...this.state.listingStatusVisibility };
        groupLayerIds.forEach(layerId => {
            listingStatusVisibility[layerId] = !anyVisible;
        });

        // Persist on the map instance so state survives legend close/reopen
        if (mapInstance) {
            mapInstance.listingStatusLayerVisibility = listingStatusVisibility;
        }
        this.setState({ listingStatusVisibility });

        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId: LISTING_STATUS_LAYER_PROPERTY,
            layerVisibility: this.listingStatusLayerVisibility(listingStatusVisibility),
            mapInstanceId: mapInstanceId,
        });
    };

    onPricingRowToggle = layerId => {
        const { mapInstance, mapInstanceId, geoJsonLegendMetadata } = this.props;
        const pricingVisibility = {
            ...this.state.pricingVisibility,
            [layerId]: this.state.pricingVisibility[layerId] === false,
        };

        if (mapInstance) {
            mapInstance.pricingLayerVisibility = pricingVisibility;
        }
        this.setState({ pricingVisibility });

        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId:
                geoJsonLegendMetadata.featurePropertyNameEquivalentToLayerId,
            layerVisibility: this.pricingLayerVisibility(pricingVisibility),
            mapInstanceId: mapInstanceId,
        });
    };

    onPricingGroupToggle = groupKey => {
        const { mapInstance, mapInstanceId, geoJsonLegendMetadata } = this.props;
        const group = PRICING_GROUPS.find(g => g.key === groupKey);
        const groupLayerIds = group.rows.map(r => r.key);
        const anyVisible = groupLayerIds.some(id => this.state.pricingVisibility[id] !== false);

        const pricingVisibility = { ...this.state.pricingVisibility };
        groupLayerIds.forEach(id => {
            pricingVisibility[id] = !anyVisible;
        });

        if (mapInstance) {
            mapInstance.pricingLayerVisibility = pricingVisibility;
        }
        this.setState({ pricingVisibility });

        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId:
                geoJsonLegendMetadata.featurePropertyNameEquivalentToLayerId,
            layerVisibility: this.pricingLayerVisibility(pricingVisibility),
            mapInstanceId: mapInstanceId,
        });
    };

    componentDidMount() {
        this.bindGluBusEvents({
            SHOULD_USE_CLUSTERS_RESPONSE: this.onShouldUseClustersResponse,
            POINTS_GEOJSON: this.onPointsFetched,
        });
        // Enable clustering toggle when opening minimized map layers panel
        this.onPointsFetched();
        this.emit('SHOULD_USE_CLUSTERS_REQUEST');
    }

    onShouldUseClustersResponse = ({ useClusters }) => {
        this.setState({ useClusters });
    };

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    // Turn on/off geoJson layer
    onVisibilityToggle = () => {
        const { mapInstanceId, mapInstance } = this.props;
        const { visible } = this.state;
        const newVisible = !visible;

        // Persist to the map instance so the state survives legend close/reopen and
        // every map-viewer rebuild, exactly as DealListSelector does for the deal pins.
        if (mapInstance) {
            mapInstance.facilityPinsVisible = newVisible;
        }
        saveMapDefaults(this.bus, { facilitiesVisible: newVisible });

        this.emit('UPDATE_GEO_JSON_LAYER_GROUP_VISIBILITY', {
            visible: newVisible,
            mapInstanceId: mapInstanceId,
        });
        this.setState({ visible: newVisible });
    };

    // Turn on/off geoJson layer clustering
    onClusterToggle = () => {
        const { mapInstanceId } = this.props;
        const { useClusters } = this.state;

        this.emit('MAP_APPLY_GEO_JSON_CLUSTERING_TOGGLE', {
            useClusters: !useClusters,
            mapInstanceId: mapInstanceId,
        });
        this.setState(state => ({
            useClusters: !state.useClusters,
        }));
    };

    // Single layer visibility toggle
    onLayerVisibilityToggle = layerId => {
        const { mapInstanceId, geoJsonLegendMetadata } = this.props;
        const { items } = this.state;

        const newItems = [...items];
        newItems.forEach(el => {
            if (el.layerId === layerId) el.visible = !el.visible;
        });

        // Persist to metadata so state survives legend close/reopen
        geoJsonLegendMetadata.layers.forEach(l => {
            const updated = newItems.find(el => el.layerId === l.layerId);
            if (updated) l.visible = updated.visible;
        });

        this.emit('UPDATE_GEO_JSON_LAYER_VISIBILITY', {
            featurePropertyNameEquivalentToLayerId:
                geoJsonLegendMetadata.featurePropertyNameEquivalentToLayerId,
            layerVisibility: newItems.map(l => ({ layerId: l.layerId, visible: l.visible })),
            mapInstanceId: mapInstanceId,
        });

        this.setState({
            items: newItems,
        });
    };

    // Clustering button gets enabled only after geoJSON is fetched
    onPointsFetched = () => {
        this.setState({ clusteringDisabled: false });
    };

    // One by-type legend row. `highlighted` rows carry the orange ring, matching the
    // *_bytype_highlighted sprites on the map.
    renderByTypeRow(group, highlighted) {
        const { byTypeHighlightMetric, byTypeVisibility } = this.state;
        const layerId = BY_TYPE_LAYER_IDS[group.key][highlighted ? 'highlighted' : 'plain'];
        const isCircle = group.shape === 'circle';
        // Operating = green-filled circle + white glyph; development = yellow-filled square
        // + WHITE outline + white glyph (matches the development_bytype_plain sprite). Both
        // types use a white glyph so they read as a consistent type-only pair.
        const chipStyle = isCircle
            ? { backgroundColor: group.color }
            : { backgroundColor: DEVELOPMENT_PIN_FILL, color: '#fff' };
        if (highlighted) {
            // box-shadow rather than a border so the ring doesn't change the chip's box
            // size — every row keeps the same height and the swatches stay aligned. 2px
            // (not the sprite's 3px) keeps the 20px chip inside its 24px swatch slot,
            // which is the same proportion the sprite's stroke has on the map.
            chipStyle.boxShadow = `0 0 0 2px ${HIGHLIGHT_COLOR}`;
        }
        const label = highlighted
            ? `${group.title} · ${BY_TYPE_HIGHLIGHT_ROW_SUFFIX[byTypeHighlightMetric]}`
            : group.title;

        return (
            <div
                key={layerId}
                className={classNames('geo-json-library-legend__status-item', {
                    'geo-json-library-legend__status-item--disabled':
                        byTypeVisibility[layerId] === false,
                })}
                onClick={() => this.onByTypeRowToggle(layerId)}
                data-testid={`by-type-row-${layerId}`}
            >
                <span className="geo-json-library-legend__status-swatch">
                    <span
                        className={classNames('geo-json-library-legend__status-swatch-chip', {
                            'geo-json-library-legend__status-swatch-chip--square': !isCircle,
                            'geo-json-library-legend__status-swatch-chip--outline': !isCircle,
                        })}
                        style={chipStyle}
                    >
                        <i className="material-symbols-filled">{group.icon}</i>
                    </span>
                </span>
                <span className="geo-json-library-legend__status-label">{label}</span>
            </div>
        );
    }

    // "By type" variant legend: one click-to-hide row per distinct pin on the map, plus a
    // "Highlight by: <current>" section. The picker is a single-select group of the three
    // metrics; the active one is reflected in the label + orange outline.
    //
    // Rows are interleaved per type — the plain row (the pins the metric does NOT match)
    // followed by its highlighted counterpart — so the pair reads as one split. A type only
    // gets a highlighted row when the active metric can match it (isTypeSplit), so with no
    // metric selected this is the plain two-row legend.
    renderByTypeLegend() {
        const { byTypeHighlightMetric } = this.state;
        const currentHighlightLabel = this.getHighlightLabel(byTypeHighlightMetric);

        const rows = [];
        BY_TYPE_GROUPS.forEach(group => {
            rows.push(this.renderByTypeRow(group, false));
            if (isTypeSplit(byTypeHighlightMetric, group.key)) {
                rows.push(this.renderByTypeRow(group, true));
            }
        });

        return (
            <div>
                {rows}
                <div className="geo-json-library-legend__highlight-section">
                    <div className="geo-json-library-legend__highlight-current">
                        <span
                            className="geo-json-library-legend__highlight-swatch"
                            style={{ borderColor: HIGHLIGHT_COLOR }}
                        />
                        <span className="geo-json-library-legend__highlight-current-label">
                            Highlight by: {currentHighlightLabel}
                        </span>
                    </div>
                    <div className="geo-json-library-legend__highlight-metric-picker">
                        {Object.values(BY_TYPE_HIGHLIGHT_METRICS).map(metric => (
                            <button
                                key={metric}
                                type="button"
                                className={classNames(
                                    'geo-json-library-legend__highlight-metric-option',
                                    {
                                        'is-selected': byTypeHighlightMetric === metric,
                                    },
                                )}
                                onClick={() => this.onByTypeHighlightMetricToggle(metric)}
                                data-testid={`by-type-highlight-metric-${metric}`}
                            >
                                {BY_TYPE_HIGHLIGHT_METRIC_LABELS[metric]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderPinConfigEditor() {
        const { pinConfig } = this.state;
        // Single subtle toggle (no dropdown): flip between Classic (legacy teardrops)
        // and Beta (by-type pins). Routes through the same onSelectPinConfig path so
        // the viewFacilityPinConfig Mixpanel event (trigger:'user_toggle') fires on each toggle.
        // Styled as a small light-blue pill (same family as the highlight metric
        // options) rather than the large filled-blue btn-flat.
        const isBeta = pinConfig === FacilityPinConfig.BY_TYPE;
        const targetConfig = isBeta ? FacilityPinConfig.LEGACY : FacilityPinConfig.BY_TYPE;
        const label = isBeta ? 'Back to legacy' : 'Try beta pins';

        return (
            <div className="geo-json-library-legend__pin-config">
                <button
                    type="button"
                    className="geo-json-library-legend__pin-config-toggle"
                    data-testid="pin-style-toggle"
                    onClick={() => this.onSelectPinConfig(targetConfig)}
                    title={label}
                    aria-label={label}
                >
                    {label}
                </button>
            </div>
        );
    }

    renderClassicLegend() {
        const { classicVisibility, items } = this.state;
        return (
            <div>
                {items.map(item => (
                    <GeoJsonLibraryLegendItem
                        key={item.layerId}
                        item={{ ...item, visible: classicVisibility[item.layerId] !== false }}
                        iconSize={24}
                        onLayerVisibilityToggle={this.onClassicRowToggle}
                    />
                ))}
            </div>
        );
    }

    renderListingStatusLegend() {
        const { listingStatusVisibility } = this.state;

        return LISTING_STATUS_GROUPS.map(group => {
            const groupLayerIds = Object.values(LISTING_STATUS_LAYER_IDS[group.key]);
            const groupVisible = groupLayerIds.some(
                layerId => listingStatusVisibility[layerId] !== false,
            );
            return (
                <div key={group.title} className="geo-json-library-legend__status-group">
                    <div
                        className={classNames('geo-json-library-legend__status-group-title', {
                            'geo-json-library-legend__status-group-title--disabled': !groupVisible,
                        })}
                        onClick={() => this.onListingStatusGroupToggle(group.key)}
                        data-testid={`listing-status-group-title-${group.key}`}
                    >
                        {group.title}
                    </div>
                    {LISTING_STATUS_ROWS.map(row => {
                        const layerId = LISTING_STATUS_LAYER_IDS[group.key][row.key];
                        const layerVisible = listingStatusVisibility[layerId] !== false;
                        // Operating = filled colored circle with a white glyph;
                        // development = yellow square (DEVELOPMENT_PIN_FILL) with a
                        // colored outline + colored glyph. The chip color is set inline so
                        // the outline variant (development) drives border + glyph via
                        // currentColor; the yellow fill comes from the shared constant.
                        const isCircle = group.shape === 'circle';
                        const chipStyle = isCircle
                            ? { backgroundColor: row.color }
                            : { backgroundColor: DEVELOPMENT_PIN_FILL, color: row.color };
                        return (
                            <div
                                key={row.key}
                                className={classNames('geo-json-library-legend__status-item', {
                                    'geo-json-library-legend__status-item--disabled': !layerVisible,
                                })}
                                onClick={() => this.onListingStatusRowToggle(layerId)}
                            >
                                <span className="geo-json-library-legend__status-swatch">
                                    <span
                                        className={classNames(
                                            'geo-json-library-legend__status-swatch-chip',
                                            {
                                                'geo-json-library-legend__status-swatch-chip--square':
                                                    group.shape === 'square',
                                                'geo-json-library-legend__status-swatch-chip--outline':
                                                    !isCircle,
                                            },
                                        )}
                                        style={chipStyle}
                                    >
                                        <i className="material-symbols-filled">{group.icon}</i>
                                    </span>
                                </span>
                                <span className="geo-json-library-legend__status-label">
                                    {row.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        });
    }

    // Mirrors renderListingStatusLegend, but each group carries its own rows
    // (operating has Has/No Pricing; development is No Pricing only) and rows
    // filter on the `category` value (row.key).
    renderPricingLegend() {
        const { pricingVisibility } = this.state;

        return PRICING_GROUPS.map(group => {
            const groupLayerIds = group.rows.map(r => r.key);
            const groupVisible = groupLayerIds.some(id => pricingVisibility[id] !== false);
            return (
                <div key={group.title} className="geo-json-library-legend__status-group">
                    <div
                        className={classNames('geo-json-library-legend__status-group-title', {
                            'geo-json-library-legend__status-group-title--disabled': !groupVisible,
                        })}
                        onClick={() => this.onPricingGroupToggle(group.key)}
                        data-testid={`pricing-group-title-${group.key}`}
                    >
                        {group.title}
                    </div>
                    {group.rows.map(row => {
                        const layerVisible = pricingVisibility[row.key] !== false;
                        const isCircle = group.shape === 'circle';
                        const chipStyle = isCircle
                            ? { backgroundColor: row.color }
                            : { backgroundColor: DEVELOPMENT_PIN_FILL, color: row.color };
                        return (
                            <div
                                key={row.key}
                                className={classNames('geo-json-library-legend__status-item', {
                                    'geo-json-library-legend__status-item--disabled': !layerVisible,
                                })}
                                onClick={() => this.onPricingRowToggle(row.key)}
                            >
                                <span className="geo-json-library-legend__status-swatch">
                                    <span
                                        className={classNames(
                                            'geo-json-library-legend__status-swatch-chip',
                                            {
                                                'geo-json-library-legend__status-swatch-chip--square':
                                                    group.shape === 'square',
                                                'geo-json-library-legend__status-swatch-chip--outline':
                                                    !isCircle,
                                            },
                                        )}
                                        style={chipStyle}
                                    >
                                        <i className="material-symbols-filled">{group.icon}</i>
                                    </span>
                                </span>
                                <span className="geo-json-library-legend__status-label">
                                    {row.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        });
    }

    render() {
        const { geoJsonLegendMetadata } = this.props;
        const { visible, items, useClusters, clusteringDisabled, pinConfig } = this.state;
        const { title } = geoJsonLegendMetadata;

        const titleClasses = classNames({
            disabled: !visible,
        });
        // The legend metadata can be an empty array
        // In that case do not show anything
        if (items.length === 0) return null;

        const isListingStatus = pinConfig === FacilityPinConfig.LISTING_STATUS;
        const isPricing = pinConfig === FacilityPinConfig.PRICING;
        const isByType = pinConfig === FacilityPinConfig.BY_TYPE;
        const isClassic = pinConfig === FacilityPinConfig.LEGACY;

        return (
            <div className="geo-json-library-legend">
                <div className="geo-json-library-legend__group">
                    <div className="geo-json-library-legend__title">
                        <div className="geo-json-library-legend__title-text">
                            <span className={titleClasses}>{title}</span>
                            {isByType && (
                                <span className="geo-json-library-legend__beta-tag">beta</span>
                            )}
                        </div>
                        <div className="flex-it center">
                            {this.renderPinConfigEditor()}
                            <VisibilityToggle
                                onClick={this.onVisibilityToggle}
                                visible={visible}
                                className="geo-json-library-legend__visibility-toggle"
                                iconClassName="material-icons-outlined"
                                testId="facilities-visibility-toggle"
                            />
                        </div>
                    </div>
                    {visible && (
                        <div className="geo-json-library-legend__items">
                            {isClassic
                                ? this.renderClassicLegend()
                                : isListingStatus
                                  ? this.renderListingStatusLegend()
                                  : isPricing
                                    ? this.renderPricingLegend()
                                    : isByType
                                      ? this.renderByTypeLegend()
                                      : items.map(item => (
                                            <GeoJsonLibraryLegendItem
                                                key={item.layerId}
                                                item={item}
                                                iconSize={24}
                                                onLayerVisibilityToggle={
                                                    this.onLayerVisibilityToggle
                                                }
                                            />
                                        ))}
                            {AppConfig.constants.shouldShowPointClusterToggle && (
                                <div className="geo-json-library-legend__item">
                                    <div className="layer-library-classes__item-icon layer-library-classes__item-icon--svg">
                                        <img
                                            src={`${AppConfig.constants.assetsBaseURL}/svg/cluster.svg`}
                                            width={24}
                                            height={24}
                                            alt="Cluster toggle icon"
                                        />
                                    </div>
                                    <span className="layer-library-classes__item-label">
                                        Clustering
                                    </span>
                                    <Toggle
                                        className="cluster-toggle"
                                        checked={useClusters}
                                        onChange={this.onClusterToggle}
                                        disabled={clusteringDisabled}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

GeoJsonLibraryLegend.propTypes = {
    geoJsonLegendMetadata: PropTypes.object.isRequired,
    mapInstanceId: PropTypes.string.isRequired,
    mapInstance: PropTypes.object,
};

export default GeoJsonLibraryLegend;
